import { Button, ButtonLink } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { CircleUser, FlaskConical, Menu, X } from 'lucide-react';
import { useEffect, useState } from 'react';
import { Link, NavLink, useLocation } from 'react-router-dom';
import { Logo } from '@/components/layout/logo';
import { Badge, Container } from '@/components/ui/primitives';
import { useSession } from '@/hooks/use-session';
import { useSite } from '@/hooks/use-site';
import { paths } from '@/lib/paths';

/**
 * En-tête du site : navigation, état de session et rappel d'environnement.
 *
 * Le badge « Environnement de test » mérite une explication : il est affiché
 * dès que `site.environment` vaut `test`. La valeur vient de `GET /site`, donc
 * de l'API elle-même — on ne devine pas l'environnement en lisant le préfixe
 * de la clé côté front. En production, le badge disparaît sans qu'aucune ligne
 * de code ait changé.
 */

const NAV_LINKS = [
    { to: paths.search, label: 'Rechercher' },
    { to: paths.pricing, label: 'Tarif' },
    { to: paths.integration, label: 'Intégration' },
    { to: paths.contact, label: 'Contact' },
];

export function Header() {
    const site = useSite();
    const { isAuthenticated, hasAccess } = useSession();
    const [mobileOpen, setMobileOpen] = useState(false);
    const location = useLocation();

    // Toute navigation referme le menu mobile : sans cela, il resterait ouvert
    // par-dessus la page suivante.
    useEffect(() => {
        setMobileOpen(false);
    }, [location.pathname]);

    return (
        <header className="border-line bg-surface/90 sticky top-0 z-30 border-b backdrop-blur">
            {site.environment === 'test' && (
                <div className="bg-brand-950 text-brand-100 px-4 py-1.5 text-center text-xs">
                    <span className="inline-flex items-center gap-1.5">
                        <FlaskConical className="size-3.5" aria-hidden />
                        Environnement de test — données d'entreprises fictives, paiements en carte de test.
                    </span>
                </div>
            )}

            <Container className="flex h-16 items-center justify-between gap-6">
                <Link to={paths.home} aria-label="DocInfoPro, retour à l'accueil">
                    <Logo />
                </Link>

                <nav aria-label="Navigation principale" className="hidden items-center gap-1 md:flex">
                    {NAV_LINKS.map((link) => (
                        <NavLink
                            key={link.to}
                            to={link.to}
                            className={({ isActive }) =>
                                cn(
                                    'rounded-lg px-3 py-2 text-sm font-medium transition-colors',
                                    isActive ? 'text-brand-700 bg-brand-50' : 'text-ink-soft hover:text-ink',
                                )
                            }
                        >
                            {link.label}
                        </NavLink>
                    ))}
                </nav>

                <div className="hidden items-center gap-3 md:flex">
                    {isAuthenticated ? (
                        <>
                            {hasAccess && (
                                <Badge tone="positive" className="hidden lg:inline-flex">
                                    Accès actif
                                </Badge>
                            )}
                            <ButtonLink to={paths.account} variant="secondary" size="sm">
                                <CircleUser className="size-4" aria-hidden />
                                Mon compte
                            </ButtonLink>
                        </>
                    ) : (
                        <>
                            <ButtonLink to={paths.login} variant="ghost" size="sm">
                                Se connecter
                            </ButtonLink>
                            <ButtonLink to={paths.pricing} size="sm">
                                Consulter une fiche
                            </ButtonLink>
                        </>
                    )}
                </div>

                <Button
                    variant="ghost"
                    size="sm"
                    className="md:hidden"
                    aria-expanded={mobileOpen}
                    aria-label={mobileOpen ? 'Fermer le menu' : 'Ouvrir le menu'}
                    onClick={() => setMobileOpen((previous) => !previous)}
                >
                    {mobileOpen ? <X className="size-5" aria-hidden /> : <Menu className="size-5" aria-hidden />}
                </Button>
            </Container>

            {mobileOpen && (
                <div className="border-line bg-surface border-t md:hidden">
                    <Container className="flex flex-col gap-1 py-4">
                        {NAV_LINKS.map((link) => (
                            <NavLink
                                key={link.to}
                                to={link.to}
                                className={({ isActive }) =>
                                    cn(
                                        'rounded-lg px-3 py-2.5 text-sm font-medium',
                                        isActive ? 'text-brand-700 bg-brand-50' : 'text-ink-soft',
                                    )
                                }
                            >
                                {link.label}
                            </NavLink>
                        ))}

                        <div className="border-line mt-3 flex flex-col gap-2 border-t pt-3">
                            {isAuthenticated ? (
                                <ButtonLink to={paths.account} variant="secondary" size="sm">
                                    Mon compte
                                </ButtonLink>
                            ) : (
                                <>
                                    <ButtonLink to={paths.login} variant="secondary" size="sm">
                                        Se connecter
                                    </ButtonLink>
                                    <ButtonLink to={paths.pricing} size="sm">
                                        Consulter une fiche
                                    </ButtonLink>
                                </>
                            )}
                        </div>
                    </Container>
                </div>
            )}
        </header>
    );
}
